// frontend/modules/template/hooks/useContextTransformation.ts
import { useCallback } from 'react';
import { 
    useTestNotificationConfigurationComplete,
    useTestContextTransformation,
    useTestTemplateProcessing
} from '@/shared/api/client/notification-configuration-controller';
import { useContextBuilderStore } from '../stores/context-builder-store';
import { 
    ContextTransformationRequest, 
    ContextOnlyTransformationRequest
} from '../types/context-transformation.types';
import {
    ContextTransformationResult,
    NotificationTestRequest,
    TemplateTestRequest
} from '@/shared/api/model';
import { message } from 'antd';

export const useContextTransformation = () => {
    const { 
        transformationResult,
        isTransforming,
        setTransformationResult,
        setIsTransforming
    } = useContextBuilderStore();
    
    // API hooks
    const { mutateAsync: transformAndProcessApi } = useTestNotificationConfigurationComplete();
    
    const { mutateAsync: transformOnlyApi } = useTestContextTransformation();
    
    const { mutateAsync: processWithTransformationApi } = useTestTemplateProcessing();
    
    // Helper function to convert local request type to backend NotificationTestRequest
    const createTestRequest = (request: ContextTransformationRequest): NotificationTestRequest => {
        // Ensure language exists, default to EN if not provided
        const language = request.language || "EN";
        
        return {
            configuration: {
                id: request.configId,
                name: request.name || "Test Configuration",
                code: request.code || "TEST_" + Date.now(),
                platform: request.platform,
                contextScriptId: request.scriptId,
                templateName: request.templateName,
                language: language, // Ensure language is set
                active: true,
                description: ""
            },
            executionRequest: {
                contextJson: request.contextJson,
                language: language // Use the same language for consistency
            },
            platform: request.platform, // Platform override
            selectedContactGroups: request.selectedContactGroups ? {
                groupKeys: request.selectedContactGroups
            } : undefined
        };
    };
    
    // Helper function to convert to TemplateTestRequest
    const createTemplateTestRequest = (request: ContextTransformationRequest): TemplateTestRequest => {
        // Ensure language exists, default to EN if not provided
        const language = request.language || "EN";
        
        return {
            configuration: {
                id: request.configId,
                name: request.name || "Test Configuration",
                code: request.code || "TEST_" + Date.now(),
                platform: request.platform,
                contextScriptId: request.scriptId,
                templateName: request.templateName,
                language: language, // Ensure language is set
                active: true,
                description: ""
            },
            transformedContext: JSON.parse(request.contextJson),
            // language: language // Use the same language for consistency
        };
    };
    
    // Public API
    const transformAndProcess = useCallback(async (request: ContextTransformationRequest) => {
        try {
            setIsTransforming(true);
            const testRequest = createTestRequest(request);
            const response = await transformAndProcessApi({ data: testRequest });
            
            if (response) {
                setTransformationResult(response);
            }
            
            setIsTransforming(false);
            return true;
        } catch (err) {
            console.error('Error in transformAndProcess:', err);
            setIsTransforming(false);
            return false;
        }
    }, [transformAndProcessApi, setIsTransforming, setTransformationResult]);
    
    const transformContextOnly = useCallback(async (request: ContextOnlyTransformationRequest) => {
        try {
            setIsTransforming(true);
            
            // Convert to the expected format
            const testRequest: NotificationTestRequest = {
                configuration: {
                    id: "",
                    name: "Test Transformation", 
                    code: "TEST_" + Date.now(), 
                    platform: "SMS",
                    contextScriptId: request.scriptId,
                    templateName: "",
                    language: "EN", // Update to use proper enum value
                    active: true,
                    description: ""
                },
                executionRequest: {
                    contextJson: request.contextJson,
                    language: "EN" // Update to use proper enum value
                }
            };
            
            const response = await transformOnlyApi({ data: testRequest });
            
            if (response) {
                setTransformationResult(response);
            }
            
            setIsTransforming(false);
            return true;
        } catch (err) {
            console.error('Error in transformContextOnly:', err);
            setIsTransforming(false);
            return false;
        }
    }, [transformOnlyApi, setIsTransforming, setTransformationResult]);
    
    const processWithContextTransformation = useCallback(async (request: ContextTransformationRequest) => {
        try {
            setIsTransforming(true);
            const testRequest = createTemplateTestRequest(request);
            const response = await processWithTransformationApi({ data: testRequest });
            
            if (response) {
                setTransformationResult(response);
            }
            
            setIsTransforming(false);
            return true;
        } catch (err) {
            console.error('Error in processWithContextTransformation:', err);
            setIsTransforming(false);
            return false;
        }
    }, [processWithTransformationApi, setIsTransforming, setTransformationResult]);
    
    // Test notification configuration with selected contact groups
    const testNotificationConfiguration = useCallback(async (request: ContextTransformationRequest & { transformedContext?: any }) => {
        try {
            setIsTransforming(true);
            
            // Create the test request with the transformed context that includes selected contact groups
            const testRequest: NotificationTestRequest = {
                configuration: {
                    id: request.configId,
                    name: request.name || "Test Configuration",
                    code: request.code || "TEST_" + Date.now(),
                    platform: request.platform,
                    contextScriptId: request.scriptId,
                    templateName: request.templateName,
                    language: request.language || "EN",
                    active: true,
                    description: ""
                },
                executionRequest: {
                    contextJson: JSON.stringify(request.transformedContext || JSON.parse(request.contextJson)),
                    language: request.language || "EN"
                }
            };
            
            const response = await transformAndProcessApi({ data: testRequest });
            
            if (response) {
                // Update the result with additional information about selected contact groups
                const baseResult = response;
                const updatedResult = {
                    ...baseResult,
                    selectedContactGroups: request.transformedContext?.selectedContactGroups
                };
                setTransformationResult(updatedResult);
                message.success('Notification configuration test completed with selected contact groups');
            }
            
            setIsTransforming(false);
            return true;
        } catch (err) {
            console.error('Error in testNotificationConfiguration:', err);
            message.error('Failed to test notification configuration');
            setIsTransforming(false);
            return false;
        }
    }, [transformAndProcessApi, setIsTransforming, setTransformationResult]);
    
    // Reset transformation result
    const resetTransformation = useCallback(() => {
        setTransformationResult(null);
    }, [setTransformationResult]);
    
    return {
        // State
        transformationResult,
        isTransforming,
        
        // Actions
        transformAndProcess,
        transformContextOnly,
        processWithContextTransformation,
        testNotificationConfiguration,
        resetTransformation
    };
};
